/**
 * Collaboration Room API Service
 * Handles all API calls for chat, tasks, files, and meetings
 */

import { API_URL } from '../config/api';

// Helper function for API calls
async function apiCall(endpoint, options = {}) {
  const url = `${API_URL}/collaboration${endpoint}`;
  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
    throw new Error(error.detail || `API error: ${response.status}`);
  }
  
  return response.json();
}

// ==================== Team Members ====================

export async function getTeamMembers() {
  return apiCall('/team-members');
}

// ==================== Messages ====================

export async function getMessages(channel = null, limit = 50) {
  const params = new URLSearchParams();
  if (channel) params.append('channel', channel);
  if (limit) params.append('limit', limit.toString());
  return apiCall(`/messages?${params.toString()}`);
}

export async function sendMessage(messageData) {
  return apiCall('/messages', {
    method: 'POST',
    body: JSON.stringify(messageData),
  });
}

export async function addReaction(messageId, emoji) {
  return apiCall(`/messages/${messageId}/reactions?emoji=${encodeURIComponent(emoji)}`, {
    method: 'POST',
  });
}

// ==================== Tasks ====================

export async function getTasks(channel = null) {
  const params = channel ? `?channel=${channel}` : '';
  return apiCall(`/tasks${params}`);
}

export async function createTask(taskData) {
  return apiCall('/tasks', {
    method: 'POST',
    body: JSON.stringify(taskData),
  });
}

export async function updateTask(taskId, updates) {
  return apiCall(`/tasks/${taskId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function deleteTask(taskId) {
  return apiCall(`/tasks/${taskId}`, {
    method: 'DELETE',
  });
}

// ==================== Files ====================

export async function getFiles(channel = null, limit = 50) {
  const params = new URLSearchParams();
  if (channel) params.append('channel', channel);
  if (limit) params.append('limit', limit.toString());
  return apiCall(`/files?${params.toString()}`);
}

export async function createFile(fileData) {
  return apiCall('/files', {
    method: 'POST',
    body: JSON.stringify(fileData),
  });
}

export async function deleteFile(fileId) {
  return apiCall(`/files/${fileId}`, {
    method: 'DELETE',
  });
}

// ==================== Meetings ====================

export async function getMeetings(project = null) {
  const params = project ? `?project=${project}` : '';
  return apiCall(`/meetings${params}`);
}

export async function getMeetingsToday() {
  return apiCall('/meetings/today');
}

export async function createMeeting(meetingData) {
  return apiCall('/meetings', {
    method: 'POST',
    body: JSON.stringify(meetingData),
  });
}

export async function deleteMeeting(meetingId) {
  return apiCall(`/meetings/${meetingId}`, {
    method: 'DELETE',
  });
}

// ==================== Activities ====================

export async function getActivities(channel = null, limit = 20) {
  const params = new URLSearchParams();
  if (channel) params.append('channel', channel);
  if (limit) params.append('limit', limit.toString());
  return apiCall(`/activities?${params.toString()}`);
}

// ==================== Projects ====================

export async function getProjects() {
  return apiCall('/projects');
}

export async function createProject(projectData) {
  return apiCall('/projects', {
    method: 'POST',
    body: JSON.stringify(projectData),
  });
}

export async function updateProject(projectId, updates) {
  return apiCall(`/projects/${projectId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

// ==================== Seed Data ====================

export async function seedCollaborationData() {
  return apiCall('/seed', {
    method: 'POST',
  });
}

// ==================== Helper: Load All Channel Messages ====================

export async function getAllMessages() {
  // Get all messages and organize by channel
  const messages = await getMessages(null, 500);
  const byChannel = {};
  
  messages.forEach(msg => {
    const channel = msg.channel;
    if (!byChannel[channel]) {
      byChannel[channel] = [];
    }
    byChannel[channel].push(msg);
  });
  
  return byChannel;
}
